import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AccountFacade } from '../../../account/store/facade';
import { LoggerService } from '../../../core/services/logger/logger.service';
import { Logger } from '../../../core/services/logger/logger.class';
import { AppConfig } from '../../../../environments/environment';
import { CompanyFlags } from 'voxloud-types/dist/models';

export type FeatureFlagName = string;

const DEFAULT_FLAGS: Record<string, boolean> = {};
const SESSION_KEY = 'featureFlags';

@Injectable({
  providedIn: 'root'
})
export class FeatureToggleService implements OnDestroy {
  private flags: Record<string, boolean> = { ...DEFAULT_FLAGS };
  private loadedCompanyId: number | null = null;
  private readonly logger: Logger | undefined;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly http: HttpClient,
    private readonly accountFacade: AccountFacade,
    loggerService: LoggerService
  ) {
    this.logger = loggerService.getLoggerInstance('FeatureToggleService');
    this.watchSession();
  }

  public isEnabled(flagName: FeatureFlagName): boolean {
    return !!this.flags[flagName];
  }

  public clearFlags(): void {
    if (this.loadedCompanyId !== null) {
      localStorage.removeItem(this.sessionKey(this.loadedCompanyId));
    }
    this.flags = { ...DEFAULT_FLAGS };
    this.loadedCompanyId = null;
    this.logger?.debug('Feature flags cleared');
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private watchSession(): void {
    this.accountFacade.session$.pipe(
      takeUntil(this.destroy$),
      map(session => session?.company_id ?? null),
      filter((companyId): companyId is number => companyId !== null),
      switchMap(companyId => {
        if (this.loadedCompanyId === companyId) return EMPTY;
        this.loadedCompanyId = companyId;
        this.flags = { ...DEFAULT_FLAGS };
        this.loadFromStorage(companyId);
        return this.http.get<CompanyFlags>(
          `${AppConfig.endpointURL}/v1/feature-flags/companies/${companyId}?detail=false`
        ).pipe(
          tap(response => {
            if (response.company_id !== companyId) {
              this.logger?.error('Feature flag response company mismatch');
              return;
            }
            this.mergeAndPersist(companyId, this.filterBooleanValues(response.flags ?? {}));
          }),
          catchError(err => {
            this.logger?.error('Failed to load feature flags:', err);
            return EMPTY;
          })
        );
      })
    ).subscribe();
  }

  private loadFromStorage(companyId: number): void {
    const stored = localStorage.getItem(this.sessionKey(companyId));
    if (stored) {
      this.flags = { ...DEFAULT_FLAGS, ...this.parseStoredFlags(stored) };
    }
  }

  private mergeAndPersist(companyId: number, newFlags: Record<string, boolean>): void {
    if (companyId !== this.loadedCompanyId) return;
    this.flags = { ...this.flags, ...newFlags };
    const key = this.sessionKey(companyId);
    const existing = this.parseStoredFlags(localStorage.getItem(key));
    localStorage.setItem(key, JSON.stringify({ ...existing, ...newFlags }));
    this.logger?.debug('Feature flags updated:', this.flags);
  }

  private parseStoredFlags(value: string | null): Record<string, boolean> {
    try {
      const parsed = JSON.parse(value ?? '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      return this.filterBooleanValues(parsed);
    } catch {
      return {};
    }
  }

  private filterBooleanValues(obj: Record<string, unknown>): Record<string, boolean> {
    return Object.fromEntries(
      Object.entries(obj).filter(([, v]) => typeof v === 'boolean')
    ) as Record<string, boolean>;
  }

  private sessionKey(companyId: number): string {
    return `${SESSION_KEY}-${companyId}`;
  }
}